import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { rescueImages } from '../mock';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const subjectLabels = {
      general: 'General Inquiry',
      adoption: 'Adoption Query',
      volunteer: 'Volunteer Opportunity',
      donation: 'Donation Information',
      partnership: 'Partnership',
      other: 'Other'
    };

    const whatsappNumber = '919315982650';
    const selectedSubject = subjectLabels[formData.subject] || formData.subject || 'Not provided';

    const message = [
      '*CONTACT FORM SUBMISSION*',
      '',
      `Name: ${formData.name || 'Not provided'}`,
      `Email: ${formData.email || 'Not provided'}`,
      `Phone: ${formData.phone || 'Not provided'}`,
      `Subject: ${selectedSubject}`,
      '',
      '*Message:*',
      formData.message || 'Not provided'
    ].join('\n');

    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');

    toast.success('Opening WhatsApp with your message details.');
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
  };

  const faqs = [
    {
      question: 'How can I report an injured animal?',
      answer: 'Call our 24/7 emergency hotline at +91 9315982650 or WhatsApp us directly. Provide the exact location and details about the animal\'s condition.'
    },
    {
      question: 'What is the adoption process?',
      answer: 'Browse our adoptable animals, submit an application, meet the animal at our facility, and complete the adoption paperwork. The entire process takes 3-5 days.'
    },
    {
      question: 'How can I volunteer?',
      answer: 'Fill out the volunteer application form on our Volunteer page. We\'ll contact you within 3 days to discuss available opportunities based on your interests and availability.'
    },
    {
      question: 'Are donations tax-deductible?',
      answer: 'Yes! Animal Care India is registered under Section 80G. We provide tax exemption certificates for all donations above ₹500.'
    },
    {
      question: 'Do you accept animal surrenders?',
      answer: 'We prioritize rescue of injured and abandoned street animals. For pet surrenders, please contact us to discuss the situation and available options.'
    },
    {
      question: 'What areas do you cover?',
      answer: 'Our primary operations are in Gurgaon and surrounding NCR areas. For emergencies outside these zones, we can provide guidance and connect you with local resources.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-forest-green/90 to-forest-green/70 z-10"></div>
        <img
          src={rescueImages[5]}
          alt="Get in Touch"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-20 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold font-heading mb-6">
            Get in Touch
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Have questions? We're here to help. Reach out to us anytime.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="border-none shadow-xl bg-sand-beige">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-warm-amber rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-blue-600 mb-3">Visit Us</h3>
                <p className="text-gray-700 leading-relaxed">
                  Animal Care Office<br />
                  House No 16, Upper Ground Floor,<br />
                  Near State Bank Of India, Sultanpur,<br />
                  New Delhi, 110030
                </p>
                <a
                  href="https://maps.google.com/maps?q=28.4967424%2C77.1615118&z=17&hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-4 text-blue-600 hover:text-blue-700 font-semibold underline"
                >
                  View on Map
                </a>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl bg-sand-beige">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-forest-green rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-forest-green mb-3">Call Us</h3>
                <p className="text-gray-700">
                  <a href="tel:+919315982650" className="text-2xl md:text-3xl font-bold text-forest-green hover:text-warm-amber block mt-2">+91 9315982650</a>
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl bg-sand-beige">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-bright-mint rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-forest-green" />
                </div>
                <h3 className="text-xl font-bold text-forest-green mb-3">Email Us</h3>
                <p className="text-gray-700">
                  <a href="mailto:info@animalcareindia.org.in" className="hover:text-warm-amber">info@animalcareindia.org.in</a>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 bg-sand-beige">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-1 gap-12 max-w-7xl mx-auto">
            {/* Contact Form */}
            <div>
              <h2 className="text-4xl font-bold font-heading text-forest-green mb-6">
                Send Us a Message
              </h2>
              <Card className="border-none shadow-xl">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div>
                      <Label htmlFor="subject">Subject *</Label>
                      <Select value={formData.subject} onValueChange={(value) => setFormData({ ...formData, subject: value })} required>
                        <SelectTrigger>
                          <SelectValue placeholder="Select subject" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General Inquiry</SelectItem>
                          <SelectItem value="adoption">Adoption Query</SelectItem>
                          <SelectItem value="volunteer">Volunteer Opportunity</SelectItem>
                          <SelectItem value="donation">Donation Information</SelectItem>
                          <SelectItem value="partnership">Partnership</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        rows={5}
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-warm-amber hover:bg-amber-600 text-white transition-colors duration-200"
                    >
                      <Send className="mr-2 w-5 h-5" />
                      Send Message
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Map & Working Hours */}
            <div className="space-y-8">
              

              <Card className="border-none shadow-xl">
                <CardContent className="p-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <Clock className="w-8 h-8 text-warm-amber" />
                    <h3 className="text-2xl font-bold text-forest-green">Working Hours</h3>
                  </div>
                  <div className="space-y-3 text-gray-700">
                    <div className="flex justify-between">
                      <span className="font-semibold">Emergency Rescue:</span>
                      <span>24/7 Available</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Office Hours:</span>
                      <span>9:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Adoption Visits:</span>
                      <span>10:00 AM - 5:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Days:</span>
                      <span>Monday - Sunday</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold font-heading text-forest-green mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-700">Quick answers to common questions</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="bg-sand-beige rounded-lg px-6 border-none">
                  <AccordionTrigger className="text-left text-lg font-semibold text-forest-green hover:text-warm-amber transition-colors">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;